import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faParking } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { dashboardMessages } from 'src/features/dashboard/utils';
import { LIST_USER_MEMBERSHIPS_RESOURCE } from 'src/features/profile/api/useListUserMemberships';
import { useAcceptInvitation } from 'src/features/userManagement/api/useAcceptInvitation';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHead,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalLabelMessages } from 'src/utils/messages';

import type { Parking } from 'src/features/parkingManagement/types/parking';
import type { Invitation } from 'src/features/userManagement/types';

interface BarrierActionModalProps {
    invitation: Invitation;
    parking: Parking;
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
}

const JoinParkingModal = ({ invitation, parking, isModalOpen, setIsModalOpen }: BarrierActionModalProps) => {
    const { formatMessage } = useIntl();
    const { toast } = useToast();
    const navigate = useNavigate();

    const { mutate, isPending } = useAcceptInvitation({
        onSuccess: (membership) => {
            onOpenChange(false);
            toast({
                title: formatMessage(dashboardMessages.parkingInvitationAcceptSuccess),
                description: formatMessage(dashboardMessages.parkingInvitationAcceptDetails, { parking: parking.name }),
            });
            void queryClient.invalidateQueries({
                queryKey: [LIST_USER_MEMBERSHIPS_RESOURCE, { userId: membership.user.id }],
            });
        },
        onError: (err) => {
            if (err?.response?.data?.detail) {
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${err.response.data.detail}`,
                });
            } else if (err?.response?.data) {
                // mapErrors(err.response.data, form.setError);
            }
        },
    });

    const onSubmit = () => {
        if (!isPending) {
            mutate({ parkingId: parking.id, invitationId: invitation.id });
        }
    };

    const onOpenChange = (isOpen: boolean) => {
        setIsModalOpen(isOpen);
        if (!isOpen) {
            void navigate('/');
        }
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogContent>
                <DialogHeader className="flex-row gap-4 space-y-0">
                    <SquareIcon icon={faParking} className="shrink-0" />
                    <DialogHead className="space-y-0">
                        <DialogTitle>{formatMessage(dashboardMessages.parkingInvitation)}</DialogTitle>
                        <DialogDescription>
                            {formatMessage(dashboardMessages.parkingInvitationSubtitle)}
                        </DialogDescription>
                    </DialogHead>
                </DialogHeader>

                <DialogSeparator />

                <DialogBody>
                    {formatMessage(dashboardMessages.parkingInvitationDetails, {
                        parking: parking.name,
                        from: invitation.inviter.name,
                    })}
                </DialogBody>

                <DialogSeparator />

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            {formatMessage(dashboardMessages.notNow)}
                        </Button>
                    </DialogClose>

                    <Button disabled={isPending} onClick={onSubmit} type="submit" className="flex-1">
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(dashboardMessages.joinParking)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default JoinParkingModal;
